import React, { createContext, useContext, useEffect, useState } from 'react';
import WebSocketService from 'services/WebsocketService';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store'; // Adjust import path
import {
  upsertCustomer,
  addMessageToCustomer,
  receivedMessageFromCustomer,
  bulkUpdateCustomers,
  customerTypingText,
  receivedStartChatFromCustomer,
  removeCustomer,
  addReceivedMessageFromAnotherLocation,
  customerEndedTheChat,
  endChatbyAgent,
  updateCustomer,
  mergeRecentCustomers,
  markAgentMessagesRead
} from 'store/slices/trafficSlice';

import { setAcceptChats } from 'store/slices/authSlice';
import { API_BASE } from 'apiConfig';
import { playIfEnabled, showDesktopNotification } from 'helpers/notifications';

import { setWebSocketService } from 'store/WebSocketServiceSingleton';

interface WebSocketContextType {
  wsService: WebSocketService | null;
  setWsService: React.Dispatch<React.SetStateAction<WebSocketService | null>>;
}

const WebSocketContext = createContext<WebSocketContextType | undefined>(
  undefined
);

export const WebSocketProvider: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const [wsService, setWsService] = useState<WebSocketService | null>(null);

  const sound1 = new Audio('https://cdn.easychatdesk.com/assets/ping1.mp3');
  const sound2 = new Audio('https://cdn.easychatdesk.com/assets/funny1.mp3');
  const sound3 = new Audio('https://cdn.easychatdesk.com/assets/new_customer.mp3');
  const sound4 = new Audio('https://cdn.easychatdesk.com/assets/beep1.mp3');
  

  const dispatch = useDispatch();
  const token = useSelector((state: RootState) => state.auth.token);

  // On app open, seed the traffic list with recent visitors (last 2 hours) so
  // agents see who was on the site, not only who's connected right now. Live WS
  // events then upgrade any of these who are still online to "active".
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    fetch(`${API_BASE}/recent-customers?hours=2`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => (r.ok ? r.json() : { customers: [] }))
      .then(data => {
        if (!cancelled && Array.isArray(data?.customers)) {
          dispatch(mergeRecentCustomers(data.customers));
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [token, dispatch]);

  useEffect(() => {
    //const token = localStorage.getItem('authToken')?.replace(/^"(.+)"$/, '$1') || ''; // Retrieve token

    if (token) {
      const handleMessage = (message: any) => {
        //console.log('Received from Socket: ', message);

        if(message.action == 'account_settings')
        {
            // payload may be [] if the backend found no user row for this uuid —
            // guard against it so the whole handler doesn't throw (which would
            // leave the toggle stuck / unresponsive).
            const settings = Array.isArray(message.payload) ? message.payload[0] : message.payload;
            if (settings && settings.online_status)
            {
              dispatch(setAcceptChats(settings.online_status === 'online'));
            }
            else
            {
              console.warn('account_settings returned no user row — online status not synced', message.payload);
            }
        }
        else if (message.action == 'agent_messages_read')
        {
          dispatch(markAgentMessagesRead({ customer_id: message.payload.customer_id }));
        }
        else if (message.action == 'update_customer_bulk')
        {
          dispatch(bulkUpdateCustomers(message.payload));
        } 
        else if (message.action == 'update_customer')
        {
          playIfEnabled(sound4);
          dispatch(updateCustomer(message.payload));
        }
        else if (message.action == 'remove_customer') 
        {
          
          dispatch(removeCustomer(message.payload));
        
        } 
        else if (message.action == 'received_start_chat_from_customer')
        {
          playIfEnabled(sound3);
          showDesktopNotification('New chat request', message.payload?.name || 'A visitor wants to chat');
          dispatch(receivedStartChatFromCustomer(message.payload));
        }
        else if (message.action == 'customer_types_text') 
        {
          dispatch(customerTypingText(message.payload));
        } 
        else if (message.action == 'received_message_from_customer') 
        {
          
          playIfEnabled(sound1);
          showDesktopNotification(
            'New message',
            message.payload?.message ||
              (message.payload?.attachment?.type === 'image'
                ? 'Sent a photo'
                : message.payload?.attachment
                  ? 'Sent an audio message'
                  : '')
          );

          const pLoad = {
            customer_id: message.payload.customer_id,
            message: message.payload.message,
            attachment: message.payload.attachment || null
          };
          dispatch(receivedMessageFromCustomer(pLoad));

        } 

        else if(message.action == "agent_has_started_chat_with_customer")
          {
            const pLoad = { customer_id: message.payload.customer_id, message: {

              id: message.payload.messageId,
              content: message.payload.message,
              type: "agent",
              agentRead: true,
              timestamp: new Date().toISOString(),
              agentName: message.payload.agent_name,
              agentAvatar: message.payload.avatar_url

            }}

            dispatch(addReceivedMessageFromAnotherLocation(pLoad));


          }
          else if(message.action == "agent_ending_chat")
          {
              const {owner_token, customer_id, text, messageId} = message.payload

              const pLoad = {
                ownerToken: owner_token,
                customerId: customer_id,
                message: text,
                messageId: messageId
              }

              console.log("is here")

              dispatch(endChatbyAgent(pLoad));


          }
        else if(message.action == "agent_writing_in_other_window")
          {

                const pLoad = { customer_id: message.payload.customer_id, message: {

                  id: message.payload.messageId,
                  content: message.payload.message,
                  type: "agent",
                  agentRead: true,
                  timestamp: new Date().toISOString(),
                  agentName: message.payload.agent_name,
                  agentAvatar: message.payload.avatar_url

                }}


            dispatch(addReceivedMessageFromAnotherLocation(pLoad));
          }
        else if (message.action == 'customer_ended_the_chat') 
        {
          dispatch(
            customerEndedTheChat({
              customer_id: message.payload.customer_id
            })
          );
        }

        if (message.type === 'customer') {
          dispatch(upsertCustomer(message.data));
        } else if (message.type === 'chat') {
          console.log(message);

          dispatch(
            addMessageToCustomer({
              customer_id: message.customerId,
              message: message.data
            })
          );
        }
      };

      const service = new WebSocketService(
        'wss://api20.easychatdesk.com/v1/customer',
        handleMessage,
        () => {
          service.sendLoginMessage(token);
        }
      );

      setWebSocketService(service);

      setWsService(service);
      service.connect();

      return () => {
        service.disconnect();
        setWebSocketService(null);
        setWsService(null);
      };
    }
  }, []);

  return (
    <WebSocketContext.Provider value={{ wsService, setWsService }}>
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};
